import { FontAwesomeIcon } from '@appcharge/shared-ui';
import {
  EPublisherSolutionModel,
  EPublisherSolutionModelSubType,
  Portal,
  Project,
  Store,
  UserRole
} from 'common/contracts';
import { UserDetails } from 'pages/Login/Login.types';
import { localStorageUtil } from 'utils/localStorageUtil';
import {
  buildPermissionsMatcher,
  resolveFeatureFlaggedPermissions
} from 'utils/permissionsUtil';

import { IPermissions } from '../../common/permissions.types';
import { EFeatureFlag, ELocalStorageKeys } from '../../constants/enums';

import {
  companyLevelNavItems,
  DailyBonus,
  pageFeatureFlagMap,
  paymentLinkProjectNavItems,
  portalLevelNavItems,
  portalLevelStoreNavItems,
  projectLevelNavItems,
  superAdminAllowedPages,
  TriggeredPopups
} from './constants';
import { EOffersPageTitle, PageTitle } from './pages';
import {
  ESideBarContext,
  ESidebarGroupKey,
  SideBarGroup,
  SideBarItem
} from './types';

export const generateProjectNavItems = (projects: Project[]): SideBarItem[] => {
  return projects
    .sort((a, b) => a.publisherName.localeCompare(b.publisherName))
    .map((project: any) => ({
      id: project.publisherId,
      title: project.publisherName,
      url: `project/${project.publisherId}`,
      icon: getIconByContext(
        ESideBarContext.PROJECT,
        project.projectType === EPublisherSolutionModel.CHECKOUT,
        project.projectSubType
      )
    }));
};

export const generatePortalNavItems = (portals: Portal[]): SideBarItem[] => {
  return portals
    .sort((a, b) => a.portalName.localeCompare(b.portalName))
    .map((portal: any) => ({
      id: portal.portalId,
      title: portal.portalName,
      url: `portal/${portal.portalId}/settings`,
      icon: getIconByContext(
        ESideBarContext.PORTAL,
        false,
        undefined,
        portal.isParkingDomain
      )
    }));
};

export const generateStarredNavItems = (
  projects: Project[],
  portals: Portal[]
): SideBarItem[] => {
  const starredIds =
    localStorageUtil.get<string[]>(ELocalStorageKeys.STARRED_PROJECTS) || [];

  const starredProjects = projects
    .filter((project) => starredIds.includes(project.publisherId))
    .map((project: any) => ({
      id: project.publisherId,
      title: project.publisherName,
      url: `project/${project.publisherId}`,
      icon: getIconByContext(
        ESideBarContext.PROJECT,
        project.projectType === EPublisherSolutionModel.CHECKOUT,
        project.projectSubType
      )
    }));

  const starredPortals = portals
    .filter((portal) => starredIds.includes(portal.portalId))
    .map((portal: Portal) => ({
      id: portal.portalId,
      title: portal.portalName,
      url: `portal/${portal.portalId}/settings`,
      icon: getIconByContext(
        ESideBarContext.PORTAL,
        false,
        undefined,
        portal.isParkingDomain
      )
    }));

  const allStarredItems = [...starredPortals, ...starredProjects];

  return allStarredItems.sort((a, b) => {
    const aIndex = starredIds.indexOf(a.id);
    const bIndex = starredIds.indexOf(b.id);
    return aIndex - bIndex;
  });
};

export const generatePortalLevelStoreNavItems = (
  stores: Store[]
): SideBarItem[] => {
  return stores
    .sort((a, b) => a.storeName.localeCompare(b.storeName))
    .map((store: any) => ({
      id: store.storeId,
      title: store.storeName,
      url: `store/${store.storeId}`,
      icon: getIconByContext(ESideBarContext.PORTAL_LEVEL_STORE)
    }));
};

export const filterGroupsByPermissions = ({
  groups,
  permissions,
  currentProjectId,
  userRole,
  featureFlags
}: {
  groups: SideBarGroup[];
  permissions: IPermissions;
  currentProjectId?: string;
  userRole?: string;
  featureFlags?: Record<EFeatureFlag, boolean>;
}): SideBarGroup[] => {
  if (!groups || groups.length < 1) {
    return [];
  }

  if (!permissions) {
    return [];
  }

  const filteredGroups = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        const configuredPermissions = item.permissions || [];
        const isPublicItem = configuredPermissions.length === 0;

        const requiredPermissions = resolveFeatureFlaggedPermissions(
          configuredPermissions,
          item.featureFlaggedPermissions,
          featureFlags
        );
        const allPermissionsGatedByFeatureFlags =
          !isPublicItem && requiredPermissions.length === 0;
        if (allPermissionsGatedByFeatureFlags) {
          return false;
        }

        const matchesRequiredPermissions = buildPermissionsMatcher(
          requiredPermissions,
          item.matchAnyPermission
        );

        let hasAllRequiredPermissions = false;
        if (!currentProjectId) {
          hasAllRequiredPermissions = Object.values(permissions).some(
            (projectPermissions) =>
              matchesRequiredPermissions((permission) =>
                Object.keys(projectPermissions).includes(permission)
              )
          );
        } else {
          const hasLocalizationPermission =
            userRole !== UserRole.SUPPORT &&
            Boolean(permissions[currentProjectId].offers?.includes('manage'));
          hasAllRequiredPermissions = matchesRequiredPermissions(
            (permission) => {
              return item.url === 'localization'
                ? hasLocalizationPermission
                : permissions[currentProjectId][permission]?.includes('view') ||
                    permissions[currentProjectId][permission]?.includes(
                      'manage'
                    );
            }
          );
        }

        return isPublicItem || hasAllRequiredPermissions;
      })
    }))
    .filter((group) => group.items.length > 0);

  return filteredGroups;
};

export const getCompanyLevelNavItems = (
  permissions: IPermissions,
  featureFlags?: Record<EFeatureFlag, boolean>,
  userDetails?: UserDetails | null
): SideBarGroup[] => {
  const permissionsFilteredGroups = filterGroupsByPermissions({
    groups: companyLevelNavItems,
    permissions,
    featureFlags
  });

  // Filter the company level nav items by feature flags
  const filteredGroups = permissionsFilteredGroups.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      const requiredFlag = pageFeatureFlagMap[item.title as PageTitle];
      if (!requiredFlag) {
        return true;
      }
      return !!featureFlags?.[requiredFlag as EFeatureFlag];
    })
  }));

  // Also need to filter the all projects nav items
  const projectsNavItems = userDetails?.projects
    ? generateProjectNavItems(userDetails.projects)
    : [];

  const portalsNavItems = userDetails?.portals
    ? generatePortalNavItems(userDetails.portals)
    : [];

  // Generate starred items
  const starredNavItems =
    userDetails?.projects && userDetails?.portals
      ? generateStarredNavItems(userDetails.projects, userDetails.portals)
      : [];

  const projectsGroup: SideBarGroup = {
    title: ESidebarGroupKey.Projects,
    isCollapsable: true,
    items: [...portalsNavItems, ...projectsNavItems]
  };

  const starredGroup: SideBarGroup = {
    title: ESidebarGroupKey.Starred,
    isCollapsable: true,
    items: starredNavItems
  };

  // Only include starred group if there are starred items, and place it after Projects
  const groupsToReturn =
    starredNavItems.length > 0
      ? [...filteredGroups, projectsGroup, starredGroup]
      : [...filteredGroups, projectsGroup];

  return groupsToReturn;
};

export const getProjectLevelNavItems = ({
  permissions,
  projectId,
  featureFlags,
  currentProjectSubType,
  hasSingleProject,
  isSuperAdmin,
  userRole
}: {
  permissions: IPermissions;
  projectId: string;
  featureFlags?: Record<EFeatureFlag, boolean>;
  currentProjectSubType?: EPublisherSolutionModelSubType | null;
  hasSingleProject: boolean;
  isSuperAdmin?: boolean;
  userRole?: string;
}): SideBarGroup[] => {
  const initialNavItems =
    currentProjectSubType === EPublisherSolutionModelSubType.PAYMENT_LINKS
      ? paymentLinkProjectNavItems(hasSingleProject)
      : projectLevelNavItems;

  const permissionsFilteredGroups = filterGroupsByPermissions({
    groups: initialNavItems,
    permissions,
    currentProjectId: projectId,
    userRole,
    featureFlags
  });

  if (!featureFlags) {
    return permissionsFilteredGroups;
  }

  return applyOffersFeatureFlags(
    permissionsFilteredGroups,
    featureFlags,
    isSuperAdmin
  );
};

const applyOffersFeatureFlags = (
  groups: SideBarGroup[],
  featureFlags: Record<string, unknown>,
  isSuperAdmin?: boolean
): SideBarGroup[] => {
  const filteredItems = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (
          isSuperAdmin &&
          superAdminAllowedPages.includes(item.title as PageTitle)
        ) {
          return true;
        }
        const requiredFlag = pageFeatureFlagMap[item.title as PageTitle];
        if (!requiredFlag) {
          return true;
        }
        return !!featureFlags[requiredFlag as EFeatureFlag];
      })
    }))
    .filter((group) => group.items.length > 0);

  if (!featureFlags[EFeatureFlag.DASHBOARD_DAILY_BONUS_SIDE_MENU]) {
    return filteredItems;
  }

  return filteredItems.map((group) => ({
    ...group,
    items: group.items.flatMap((item) =>
      item.title === EOffersPageTitle.POPUPS
        ? [TriggeredPopups, DailyBonus]
        : [item]
    )
  }));
};

export const getPortalLevelNavItems = (
  permissions: IPermissions,
  portalId?: string,
  userDetails?: UserDetails | null
): SideBarGroup[] => {
  // todo: filter by permissions when defined
  if (!portalId) {
    return [];
  }

  if (!userDetails) {
    console.warn(
      'getPortalLevelNavItems: userDetails is required. Use useUserDetails() hook to get user data from React Query.'
    );
    return [];
  }

  const assignedStores = userDetails?.portals?.find(
    (portal) => portal.portalId === portalId
  )?.stores;

  const portalLevelStoreNavItems = assignedStores
    ? generatePortalLevelStoreNavItems(assignedStores)
    : [];

  const storesGroup: SideBarGroup = {
    title: ESidebarGroupKey.Stores,
    isCollapsable: true,
    items: portalLevelStoreNavItems
  };

  return filterGroupsByPermissions({
    groups: [...portalLevelNavItems, storesGroup],
    permissions,
    userRole: userDetails?.role
  });
};

export const getPortalLevelStoreNavItems = ({
  featureFlags,
  isSuperAdmin
}: {
  featureFlags?: Record<string, unknown>;
  isSuperAdmin?: boolean;
} = {}): SideBarGroup[] => {
  if (!featureFlags) {
    return portalLevelStoreNavItems;
  }

  return applyOffersFeatureFlags(
    portalLevelStoreNavItems,
    featureFlags,
    isSuperAdmin
  );
};

export const getIconByContext = (
  context: ESideBarContext,
  isCheckout: boolean = false,
  projectSubType?: EPublisherSolutionModelSubType,
  isParkingDomain?: boolean
) => {
  switch (context) {
    case ESideBarContext.PORTAL:
      if (isParkingDomain) {
        return (
          <div
            className={`w-6 h-6 bg-sky-100 rounded-full flex items-center justify-center`}
          >
            <FontAwesomeIcon
              icon="fa-regular fa-money-check-dollar"
              className="text-[12px] text-zinc-600"
            />
          </div>
        );
      }
      return (
        <div
          className={`w-6 h-6 bg-violet-100 rounded-full flex items-center justify-center`}
        >
          <FontAwesomeIcon
            icon="fa-regular fa-table-layout"
            className="text-[12px] text-zinc-600"
          />
        </div>
      );
    case ESideBarContext.PROJECT:
      if (isCheckout) {
        // Check if it's a payment link
        if (projectSubType === EPublisherSolutionModelSubType.PAYMENT_LINKS) {
          return (
            <div
              className={`w-6 h-6 bg-zinc-200 rounded-full flex items-center justify-center`}
            >
              <FontAwesomeIcon
                icon="fa-regular fa-square-up-right"
                className="text-[12px] text-zinc-600"
              />
            </div>
          );
        }
        // Check if it's side loading
        if (projectSubType === EPublisherSolutionModelSubType.SIDE_LOADING) {
          return (
            <div
              className={`w-6 h-6 bg-orange-200 rounded-full flex items-center justify-center`}
            >
              <FontAwesomeIcon
                icon="fa-regular fa-arrow-down-from-arc"
                className="text-[12px] text-zinc-600"
              />
            </div>
          );
        }
        // Otherwise it's a web checkout
        return (
          <div
            className={`w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center`}
          >
            <FontAwesomeIcon
              icon="fa-regular fa-credit-card"
              className="text-[12px] text-zinc-600"
            />
          </div>
        );
      } else {
        return (
          <div
            className={`w-6 h-6 bg-green-200 rounded-full flex items-center justify-center`}
          >
            <FontAwesomeIcon
              icon="fa-regular fa-store"
              className="text-[12px] text-zinc-600"
            />
          </div>
        );
      }
    case ESideBarContext.PORTAL_LEVEL_STORE:
      return (
        <div
          className={`w-6 h-6 bg-green-200 rounded-full flex items-center justify-center`}
        >
          <FontAwesomeIcon
            icon="fa-regular fa-store"
            className="text-[12px] text-zinc-600"
          />
        </div>
      );

    default:
      return null;
  }
};
